import { useRef, useEffect } from "react"

// `front` renders the device square-on with no tilt. Used where this is
// standing in for the 3D phone (the AI Lab WebGL fallback): the 3D
// version's angle comes from a live camera, and a fixed CSS tilt reads
// as a different device rather than a substitute for it, so straight-on
// is the neutral, honest stand-in. Launched keeps the default tilt.
// `videoRef` / `loop` / `onEnded` exist for story playback: when a
// project has several clips the parent owns which one is showing and
// needs the element to read progress from, so it passes its own ref and
// turns looping off — each clip has to end for the next to start.
export default function IPhoneDevice({
  src,
  front = false,
  videoRef: externalRef,
  loop = true,
  onEnded,
}) {
  const localRef = useRef(null)
  const videoRef = externalRef || localRef

  useEffect(() => {
    const v = videoRef.current
    if (!v) return

    // No v.load() here. The element already carries autoPlay, so calling
    // load() aborts the play attempt that's in flight and resets the
    // element — "The play() request was interrupted by a new load
    // request" — which can leave the video stuck on a blank frame.
    // React updates src on the element itself, so no manual reload is
    // needed anyway.
    const tryPlay = () => {
      if (v.paused) v.play().catch(() => {})
    }
    tryPlay()

    // iOS Safari in particular refuses autoplay in Low Power Mode until
    // there's been a user gesture, so retry on the events that grant one
    // rather than assuming the first attempt stuck.
    const events = ["pointerdown", "touchstart", "keydown"]
    events.forEach((e) => document.addEventListener(e, tryPlay, { passive: true }))
    v.addEventListener("canplay", tryPlay)
    v.addEventListener("loadeddata", tryPlay)
    return () => {
      events.forEach((e) => document.removeEventListener(e, tryPlay))
      v.removeEventListener("canplay", tryPlay)
      v.removeEventListener("loadeddata", tryPlay)
    }
  }, [src, videoRef])

  return (
    <div className="ip-wrap">
      <div className={`ip-body${front ? " ip-body--front" : ""}`}>
        <div className="ip-btn ip-btn--silence" />
        <div className="ip-btn ip-btn--vol-up" />
        <div className="ip-btn ip-btn--vol-down" />
        <div className="ip-btn ip-btn--power" />
        <div className="ip-screen">
          <div className="ip-glare" />
          <video
            ref={videoRef}
            className="ip-video"
            src={src}
            autoPlay
            loop={loop}
            onEnded={onEnded}
            muted
            playsInline
          />
        </div>
      </div>
    </div>
  )
}
